// config/business-types/esg-compliance.config.js

export const ESG_COMPLIANCE_CONFIG = {
  name: 'ESG Compliance',
  description: 'VSME and EU CSRD compliance lead generation',
  
  // Data sources prioritized for ESG customers
  dataSources: {
    primary: ['companiesHouse', 'vies'],
    secondary: ['germanRegistry', 'frenchRegistry'],
    enrichment: ['cdp', 'esef']
  },

  // Company size thresholds for VSME and CSRD
  complianceThresholds: {
    vsme: {
      employees: { min: 10, max: 250 },
      turnover: { min: 2000000, max: 50000000 }, // €2M - €50M
      balanceSheet: { min: 2000000, max: 43000000 } // €2M - €43M
    },
    csrd: {
      large: {
        employees: 500,
        turnover: 40000000, // €40M
        balanceSheet: 20000000 // €20M
      },
      listed: {
        // All listed SMEs (delayed to 2026)
        minEmployees: 10
      }
    }
  },

  // High ESG risk industries (prioritize for outreach)
  highRiskIndustries: [
    { code: '01-03', name: 'Agriculture, forestry and fishing', riskLevel: 'HIGH' },
    { code: '05-09', name: 'Mining and quarrying', riskLevel: 'HIGH' },
    { code: '10-33', name: 'Manufacturing', riskLevel: 'HIGH' },
    { code: '35', name: 'Electricity, gas, steam and air conditioning', riskLevel: 'HIGH' },
    { code: '36-39', name: 'Water supply; sewerage, waste management', riskLevel: 'HIGH' },
    { code: '41-43', name: 'Construction', riskLevel: 'MEDIUM' },
    { code: '49-53', name: 'Transportation and storage', riskLevel: 'MEDIUM' }
  ],

  // Scoring weights for ESG leads
  scoringWeights: {
    complianceUrgency: 40, // CSRD deadline urgency
    industryRisk: 30,      // ESG risk level of industry
    companySize: 20,       // Size indicates reporting complexity
    geographicProximity: 10 // Distance from customer location
  },

  // CSRD implementation timeline
  csrdDeadlines: {
    2024: 'Large companies (>500 employees) - First reporting',
    2025: 'Large companies - Full compliance',
    2026: 'Listed SMEs - First reporting',
    2027: 'Listed SMEs - Full compliance',
    2028: 'Non-EU subsidiaries - First reporting'
  },

  // ESG reporting categories (VSME aligned)
  esgCategories: {
    environmental: [
      'emissions',
      'energy',
      'water',
      'waste',
      'biodiversity'
    ],
    social: [
      'employees',
      'equality',
      'humanRights',
      'communityImpact'
    ],
    governance: [
      'boardComposition',
      'executiveCompensation',
      'businessEthics',
      'riskManagement'
    ]
  },

  // Default search filters for ESG customer
  defaultFilters: {
    complianceType: 'all', // 'csrd_mandatory', 'vsme_eligible', 'approaching_threshold'
    industryRisk: 'all',   // 'high', 'medium', 'low'
    reportingDeadline: 'all', // '2024', '2025', '2026'
    country: 'all',
    companySize: 'all'
  },

  // AI insight prompts for ESG context
  insightPrompts: {
    regulatoryDeadline: 'Analyze CSRD compliance timeline and urgency for this company',
    industryRisk: 'Assess ESG risks specific to this industry sector',
    competitorBenchmark: 'Compare ESG maturity against industry peers',
    reportingGaps: 'Identify potential ESG reporting challenges',
    opportunitySize: 'Estimate ESG software opportunity value'
  }
};

// Export individual configs for modularity
export const VSME_THRESHOLDS = ESG_COMPLIANCE_CONFIG.complianceThresholds.vsme;
export const CSRD_THRESHOLDS = ESG_COMPLIANCE_CONFIG.complianceThresholds.csrd;
export const HIGH_RISK_INDUSTRIES = ESG_COMPLIANCE_CONFIG.highRiskIndustries;
export const ESG_SCORING_WEIGHTS = ESG_COMPLIANCE_CONFIG.scoringWeights;